// ============================================================
// WHAT THE STEALTH MAP SHOWS OF THE OPPOSITION'S MOOD (plan design-gaps, Z2;
// the stealth page): each watcher's word when it is roused - INVESTIGATING,
// SEARCHING - for the tag under its figure; the noise the wedges turn to (a
// loud attack's, or a Distract's where it landed); and the party leader's
// sweep, quicker the higher the ladder.
//
// Pure. Zero React dependency.
// ============================================================

import { oppStateOf, RUNGS, type Rung, type StealthState } from './ladder';
import { DEFAULT_SIGNATURE_REACH, reachOf, type Signature, type SignatureReach } from './noise';

/** The words a watcher's figure wears when roused; at rest it says nothing. */
const ROUSED: Partial<Record<string, string>> = { investigating: 'INVESTIGATING', searching: 'SEARCHING' };

/** Each watcher's word, by id, only for the roused (the GM's word for them, else the rung's). */
export function alertWords(state: StealthState, ids: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const id of ids) {
    const word = ROUSED[oppStateOf(state, id)];
    if (word) out[id] = word;
  }
  return out;
}

/** A noise somebody may turn to: where it is, and how far it carries (the step's reach at this table, metres). */
export interface NoiseAt {
  x: number;
  z: number;
  reach: number;
}

/** Whether a figure stands within a noise's reach (Z2: the watchers that turn to it). */
export const hearsNoise = (at: { x: number; z: number }, heard: NoiseAt | null): boolean =>
  !!heard && Math.hypot(heard.x - at.x, heard.z - at.z) <= heard.reach;

/** What a beat needs for its noise: who made it, where it landed, how loud, what it was. */
interface Heard {
  from: { x: number; z: number } | null;
  floor: { x: number; z: number };
  noise: 'loud' | 'silent' | null;
  /** The weapon's step (plan armoury), when the line says it; else the coarse word stands for it. */
  signature?: Signature | null;
  attack: string;
  attackerId: string | null;
  targetId: string;
  at: number;
}

/**
 * Where the wedges turn (the stealth page's "wedges turn to a noise"): the newest of the beats playing that made one -
 * a Distract where it landed, a loud attack where it came from - or null. A quiet blade or a Distract with nowhere to
 * land turns nobody.
 */
export function noiseHeard(beats: readonly Heard[], reach: SignatureReach = DEFAULT_SIGNATURE_REACH): NoiseAt | null {
  const newest = beats.filter(madeANoise).reduce<Heard | null>((n, b) => (!n || b.at > n.at ? b : n), null);
  if (!newest) return null;
  // A Distract carries as a loud noise does where it lands; an attack as far as its weapon's step.
  if (newest.attack === 'distract') return { ...newest.floor, reach: reach.loud };
  const step = newest.signature ?? 'loud';
  return newest.from ? { ...newest.from, reach: reachOf(step, reach) } : null;
}

/** A Distract that landed somewhere, or a loud attack from somewhere this screen knows. */
const madeANoise = (b: Heard) =>
  (b.attack === 'distract' && b.targetId !== b.attackerId) || (b.noise === 'loud' && !!b.from);

/** The leader's sweep goes round once in this many seconds, by rung: the higher the ladder, the quicker (8, 5, 3, 1.5). */
const LEADER_SWEEP_S = [8, 5, 3, 1.5];
export const leaderSweepPeriod = (rung: Rung): number => LEADER_SWEEP_S[RUNGS.indexOf(rung)] ?? LEADER_SWEEP_S[0];
